import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { getAvailableSupport } from "../../../../api";
import supportTypes from "./supportTypes.json";
import { supabase } from "../../../../supabase";
import CreateClickupTicket from "../../../shared/CreateClickupTicket";

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onClose
 * @param root0.dealer
 */
/**
 * SupportModal Component
 *
 * This component renders a modal dialog for submitting support applications.
 * It allows users to select support types, input values, and submit the application.
 *
 * @component
 * @param {object} props - The component props.
 * @param {boolean} props.open - Determines whether the modal is open or closed.
 * @param {Function} props.onClose - Callback function to close the modal.
 * @param {object} props.dealer - The dealer object containing dealer details.
 * @param {number} props.dealer.id - The ID of the dealer.
 * @param {string} props.dealer.first_name - The name of the dealer.
 * @param {string} props.dealer.surname - The surname of the dealer.
 *
 *
 * @returns {Element} The rendered SupportModal component.
 *
 * @example
 * <SupportModal
 *   open={isModalOpen}
 *   onClose={handleModalClose}
 *   dealer={{ id: 123, name: "John", surname: "Doe" }}
 * />
 *
 * @function
 * @name SupportModal
 *
 * @description
 * - Fetches available support options for the dealer on component mount.
 * - Allows users to select support types and input corresponding values.
 * - Submits the support application to the database and creates a ClickUp ticket.
 * - Handles loading state and error handling during submission.
 *
 * @dependencies
 * - React hooks: useState, useEffect
 * - Material-UI components: Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Button
 * - External functions: getAvailableSupport, supabase, CreateClickupTicket
 */
export default function SupportModal({
  open,
  onClose,
  dealer: { id, first_name, surname },
}) {
  const [loading, setLoading] = useState(false);
  const [support, setSupport] = useState([]);
  const [newSupport, setNewSupport] = useState([]);

  useEffect(() => {
    const getSupport = async () => {
      const response = await getAvailableSupport({ user_id: id });
      if (response) {
        setSupport(response.data);
      }
    };
    getSupport();
  }, [id]);

  const handleSubmit = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .schema("finance")
      .from("support_applications")
      .insert([
        {
          agent_z_code: id,
          agent_name: first_name,
          agent_surname: surname,
          items_amount: newSupport.length,
          support_1_type: newSupport[0]?.name || null,
          support_1_value: newSupport[0]?.value || null,
          support_2_type: newSupport[1]?.name || null,
          support_2_value: newSupport[1]?.value || null,
          support_3_type: newSupport[2]?.name || null,
          support_3_value: newSupport[2]?.value || null,
          support_4_type: newSupport[3]?.name || null,
          support_4_value: newSupport[3]?.value || null,
          support_5_type: newSupport[4]?.name || null,
          support_5_value: newSupport[4]?.value || null,
        },
      ])
      .select()
      .single();

    CreateClickupTicket(
      `New Support Application for ${id}: ${first_name} ${surname}`,
      "901503420078",
      `New support request, click here to generate the contract: https://finance.zawadi.africa/supportContractGenerator?id=${data.id}`
    );
    if (error) {
      console.error("Error inserting support application:", error);
      setLoading(false);
      return;
    }
    setLoading(false);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle align="center">New Support Application</DialogTitle>
      <DialogContent>
        <Typography variant="h5" align="center">
          Available support: R {support}
        </Typography>
        <br />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              label="Select Support Type"
              fullWidth
              SelectProps={{
                native: true,
              }}
              variant="outlined"
              onChange={e => {
                const selectedType = supportTypes.find(
                  type => type.id === Number(e.target.value)
                );
                if (selectedType) {
                  setNewSupport(prevState => [
                    ...prevState,
                    { name: selectedType.name, value: 0 },
                  ]);
                }
              }}
              disabled={newSupport.length >= 5}
            >
              <option value="" disabled>
                -- Select an option --
              </option>
              {supportTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            {newSupport.map((supportItem, index) => (
              <Grid
                container
                spacing={1}
                alignItems="center"
                key={supportItem.name}
              >
                <Grid item xs={9}>
                  <TextField
                    label={supportItem.name}
                    fullWidth
                    variant="outlined"
                    style={{ marginBottom: 16 }}
                    onChange={e => {
                      setNewSupport(prevState => {
                        return prevState.map((item, idx) => {
                          if (idx === index) {
                            return { ...item, value: e.target.value };
                          }
                          return item;
                        });
                      });
                    }}
                    value={supportItem.value}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setNewSupport(prevState =>
                        prevState.filter((_, idx) => idx !== index)
                      );
                    }}
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          variant="contained"
          disabled={loading}
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
